import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { PortalAuthProvider, usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalLoginPage from './PortalLoginPage';
import PortalOrdersPage from './PortalOrdersPage';
import PortalOrderDetailPage from './PortalOrderDetailPage';
import PortalNewOrderPage from './PortalNewOrderPage';
import PortalChatPage from './PortalChatPage';
import { ChangePasswordPage } from './PortalShared';

/** Redirects unauthenticated users to the portal login page. */
const PortalProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { customer, loading } = usePortalAuth();
  const { orgAlias } = useParams<{ orgAlias: string }>();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-gray-400 bg-slate-50">Loading…</div>
  );
  if (!customer) return <Navigate to={`/portal/${orgAlias}/login`} replace />;
  return <>{children}</>;
};

/** Inner routes — useParams works here because we're already under /portal/:orgAlias */
const PortalInnerRoutes: React.FC = () => {
  const { orgAlias } = useParams<{ orgAlias: string }>();

  return (
    <PortalAuthProvider orgAlias={orgAlias!}>
      <Routes>
        <Route path="login" element={<PortalLoginPage />} />
        <Route path="change-password" element={<PortalProtectedRoute><ChangePasswordPage /></PortalProtectedRoute>} />
        <Route path="orders" element={<PortalProtectedRoute><PortalOrdersPage /></PortalProtectedRoute>} />
        <Route path="orders/new" element={<PortalProtectedRoute><PortalNewOrderPage /></PortalProtectedRoute>} />
        <Route path="orders/:orderId" element={<PortalProtectedRoute><PortalOrderDetailPage /></PortalProtectedRoute>} />
        <Route path="chat" element={<PortalProtectedRoute><PortalChatPage /></PortalProtectedRoute>} />
        {/* Default: redirect to orders list */}
        <Route path="*" element={<Navigate to={`/portal/${orgAlias}/orders`} replace />} />
      </Routes>
    </PortalAuthProvider>
  );
};

export default PortalInnerRoutes;
